import {
  cursorToScreenshotPixel,
  type ScreenshotScreenMeta,
} from "@/lib/pointing-transform";

function pointExtent(
  exact: number | undefined,
  nativePixels: number,
  scaleFactor: number,
): number {
  return exact && Number.isFinite(exact) && exact > 0
    ? exact
    : nativePixels / scaleFactor;
}

function containsCursor(
  screen: ScreenshotScreenMeta,
  cursorX: number,
  cursorY: number,
): boolean {
  const width = pointExtent(
    screen.widthPoints,
    screen.widthPx,
    screen.scaleFactor,
  );
  const height = pointExtent(
    screen.heightPoints,
    screen.heightPx,
    screen.scaleFactor,
  );
  return (
    cursorX >= screen.originX &&
    cursorX < screen.originX + width &&
    cursorY >= screen.originY &&
    cursorY < screen.originY + height
  );
}

export function buildScreenshotPointerToken(
  screens: ScreenshotScreenMeta[],
  cursorX: number,
  cursorY: number,
): string | null {
  const screen =
    screens.find(
      (candidate) =>
        candidate.isCursorScreen && containsCursor(candidate, cursorX, cursorY),
    ) ??
    screens.find((candidate) => containsCursor(candidate, cursorX, cursorY));
  if (!screen) return null;

  const x = cursorToScreenshotPixel(
    cursorX,
    screen.originX,
    screen.scaleFactor,
    screen.widthPx,
    screen.scaledWidth,
    screen.widthPoints,
  );
  const y = cursorToScreenshotPixel(
    cursorY,
    screen.originY,
    screen.scaleFactor,
    screen.heightPx,
    screen.scaledHeight,
    screen.heightPoints,
  );
  return `[POINTER:screen=${screen.index + 1},x=${x},y=${y}]`;
}

export function globalPointToScreenshotPoint(
  screen: ScreenshotScreenMeta,
  point: { x: number; y: number },
): { x: number; y: number } | null {
  const width = pointExtent(
    screen.widthPoints,
    screen.widthPx,
    screen.scaleFactor,
  );
  const height = pointExtent(
    screen.heightPoints,
    screen.heightPx,
    screen.scaleFactor,
  );
  if (
    point.x < screen.originX ||
    point.x > screen.originX + width ||
    point.y < screen.originY ||
    point.y > screen.originY + height
  ) {
    return null;
  }
  return {
    x: cursorToScreenshotPixel(
      point.x,
      screen.originX,
      screen.scaleFactor,
      screen.widthPx,
      screen.scaledWidth,
      screen.widthPoints,
    ),
    y: cursorToScreenshotPixel(
      point.y,
      screen.originY,
      screen.scaleFactor,
      screen.heightPx,
      screen.scaledHeight,
      screen.heightPoints,
    ),
  };
}

/** Screenshot pixels per screen point across the display's width: how much a
 *  length drawn on screen grows or shrinks in the (capped) screenshot. */
export function screenshotPixelsPerPoint(screen: ScreenshotScreenMeta): number {
  return (
    screen.scaledWidth /
    pointExtent(screen.widthPoints, screen.widthPx, screen.scaleFactor)
  );
}
